import { ArrowRight, BusFront, Check, Clock3, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import {
  getDirections,
  getStops,
  isScheduleAvailable,
  ROUTES,
  SCHEDULE_VALID_FROM,
  SCHEDULE_VALID_TO,
} from './data/timetable';
import { useBusTimes, useBusTimesActions } from './state';
import type { RouteId, ScheduledDeparture } from './state/events';

type RouteOption = { id: RouteId; name: string };
type DirectionOption = { id: string; label: string };
type StopOption = { id: string; name: string };

const seattleTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
});

const scheduleDate = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function formatDepartureTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time unavailable';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function StageRail({ current }: { current: number }) {
  const stages = ['Route', 'Direction', 'Stop', 'Departures'];
  return (
    <aside className="border-b border-border bg-muted/35 p-5 md:w-52 md:border-b-0 md:border-r md:p-6">
      <div className="flex items-center justify-between gap-3 md:block">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Your trip</p>
          <h2 className="mt-2 text-lg font-semibold tracking-[-0.02em]">Build a departure</h2>
        </div>
        <div className="flex gap-1.5 md:mt-8 md:block md:space-y-3">
          {stages.map((stage, index) => (
            <div key={stage} className="flex items-center gap-3 md:gap-4">
              <span
                className={`flex size-7 shrink-0 items-center justify-center rounded-md border text-xs font-bold tabular-nums ${index < current ? 'border-primary bg-primary text-primary-foreground' : index === current ? 'border-foreground bg-foreground text-background' : 'border-border bg-background text-muted-foreground'}`}
              >
                {index < current ? <Check className="size-4" aria-hidden="true" /> : index + 1}
              </span>
              <span
                className={`hidden text-sm md:inline ${index === current ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
              >
                {stage}
              </span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-5 max-w-[28ch] text-sm leading-6 text-muted-foreground md:mt-10">
        Choose a route, direction and stop to see the next scheduled buses.
      </p>
    </aside>
  );
}

function ChoiceButton({
  label,
  detail,
  selected,
  onClick,
  journeyKey,
}: {
  label: string;
  detail?: string;
  selected: boolean;
  onClick: () => void;
  journeyKey: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      aria-pressed={selected}
      data-jr-key={journeyKey}
      onClick={onClick}
      className="h-auto min-h-16 w-full justify-between rounded-md border-border bg-background px-4 py-3 text-left hover:border-foreground hover:bg-muted/50 aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background"
    >
      <span className="min-w-0">
        <span className="block truncate text-base font-semibold">{label}</span>
        {detail ? (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground aria-pressed:text-background/70">
            {detail}
          </span>
        ) : null}
      </span>
      {selected ? (
        <Check className="size-5 shrink-0" aria-hidden="true" />
      ) : (
        <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      )}
    </Button>
  );
}

function DepartureRow({ departure, index }: { departure: ScheduledDeparture; index: number }) {
  return (
    <li className="grid grid-cols-[auto_1fr_auto] items-center gap-4 border-t border-border py-4 first:border-t-0">
      <span className="flex size-8 items-center justify-center rounded-sm bg-muted text-xs font-bold tabular-nums text-muted-foreground">
        {String(index + 1).padStart(2, '0')}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">To {departure.destination}</p>
        <p className="mt-1 text-xs text-muted-foreground">Scheduled departure</p>
      </div>
      <div className="text-right">
        <p className="text-2xl font-bold tracking-[-0.03em] tabular-nums">{formatDepartureTime(departure.time)}</p>
        <p className="mt-1 text-xs font-medium tabular-nums text-muted-foreground">{departure.minutesAway} min away</p>
      </div>
    </li>
  );
}

function SchedulePane({
  departures,
  checkedAt,
  available,
  onView,
  onRefresh,
  canView,
  hasViewed,
}: {
  departures: ScheduledDeparture[];
  checkedAt: string | null;
  available: boolean;
  onView: () => void;
  onRefresh: () => void;
  canView: boolean;
  hasViewed: boolean;
}) {
  return (
    <section className="border-t border-border p-5 md:p-8" aria-labelledby="schedule-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">04 / Departures</p>
          <h2 id="schedule-heading" className="mt-2 text-2xl font-bold tracking-[-0.03em]">
            Next scheduled buses
          </h2>
        </div>
        {hasViewed ? (
          <Button type="button" variant="outline" size="sm" onClick={onRefresh} data-jr-key="refresh-schedule">
            <RefreshCw className="size-3.5" aria-hidden="true" /> Refresh
          </Button>
        ) : null}
      </div>
      {!hasViewed ? (
        <div className="mt-6 flex flex-col items-start gap-4 border border-dashed border-border bg-muted/20 p-5">
          <p className="max-w-md text-sm leading-6 text-muted-foreground">
            This board uses the bundled schedule for Seattle local time. It does not track buses in real time.
          </p>
          <Button type="button" onClick={onView} data-jr-key="view-schedule" disabled={!canView}>
            <Clock3 className="size-4" aria-hidden="true" /> View scheduled buses
          </Button>
        </div>
      ) : !available ? (
        <div className="mt-6 border border-dashed border-border bg-muted/20 p-5">
          <p className="font-semibold">This timetable is outside its published dates.</p>
          <p className="mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
            Scheduled departures are not available right now. Check back when a current timetable is published.
          </p>
        </div>
      ) : departures.length === 0 ? (
        <div className="mt-6 border border-dashed border-border bg-muted/20 p-5">
          <p className="font-semibold">No scheduled buses are documented for this selection.</p>
          <p className="mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
            Try another stop or direction. This board does not fill gaps with estimated arrivals.
          </p>
        </div>
      ) : (
        <>
          <ol className="mt-5" aria-label="Scheduled departures">
            {departures.slice(0, 3).map((departure, index) => (
              <DepartureRow key={`${departure.tripId}-${departure.time}`} departure={departure} index={index} />
            ))}
          </ol>
          <div className="mt-5 flex items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground">
            <BusFront className="size-4" aria-hidden="true" /> Scheduled, not live · Seattle local time
          </div>
        </>
      )}
      <p className="mt-5 text-xs text-muted-foreground">
        Timetable published {scheduleDate.format(new Date(SCHEDULE_VALID_FROM))}–
        {scheduleDate.format(new Date(SCHEDULE_VALID_TO))}.
        {checkedAt ? ` Last checked ${seattleTime.format(new Date(checkedAt))}.` : ''}
      </p>
    </section>
  );
}

export function BusTimesPage() {
  const access = useSurfaceAccess(SURFACES['bus-times']);
  const snapshot = useBusTimes((current) => current);
  const { chooseRoute, chooseDirection, chooseStop, viewSchedule, refreshSchedule } = useBusTimesActions();
  const { routeId, directionId, stopId, departures, checkedAt } = snapshot.context;
  const directions = routeId ? getDirections(routeId) : [];
  const stops = routeId && directionId ? getStops(routeId, directionId) : [];
  const available = isScheduleAvailable(new Date());
  const currentStage = snapshot.matches('route')
    ? 0
    : snapshot.matches('direction')
      ? 1
      : snapshot.matches('stop')
        ? 2
        : 3;
  const hasViewed = snapshot.matches('ready') || snapshot.matches('unavailable');

  if (access === 'waiting')
    return (
      <main className="min-h-screen bg-background p-6 text-foreground">
        <p className="mx-auto max-w-3xl py-20 text-center text-sm text-muted-foreground">
          Checking access to the departure board…
        </p>
      </main>
    );
  if (access === 'unavailable')
    return (
      <main className="min-h-screen bg-background p-6 text-foreground">
        <p className="mx-auto max-w-3xl py-20 text-center text-sm text-muted-foreground">
          The departure board is temporarily unavailable. Please try again shortly.
        </p>
      </main>
    );
  if (access === 'denied')
    return (
      <main className="min-h-screen bg-background p-6 text-foreground">
        <p className="mx-auto max-w-3xl py-20 text-center text-sm text-muted-foreground">
          You do not have access to this departure board.
        </p>
      </main>
    );

  return (
    <main className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      <div className="mx-auto max-w-[1120px] px-4 py-5 sm:px-6 lg:px-8">
        <header className="flex items-center justify-between border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-sm bg-primary text-primary-foreground">
              <BusFront className="size-4" aria-hidden="true" />
            </span>
            <p className="text-sm font-bold tracking-[0.12em]">SEATTLE / BUS TIMES</p>
          </div>
          <p className="hidden text-xs text-muted-foreground sm:block">A schedule, not a live feed</p>
        </header>
        <div className="py-10 md:py-16">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-[-0.045em] sm:text-5xl">Know the next scheduled bus.</h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-muted-foreground">
              Build a trip in three quick choices, then read the timetable in Seattle local time.
            </p>
          </div>
          <div className="mt-8 flex flex-col border border-border bg-card md:flex-row">
            <StageRail current={currentStage} />
            <div className="min-w-0 flex-1">
              <section className="p-5 md:p-8" aria-labelledby="route-heading">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">01 / Route</p>
                <h2 id="route-heading" className="mt-2 text-2xl font-bold tracking-[-0.03em]">
                  Where are you riding?
                </h2>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {ROUTES.map((route: RouteOption) => (
                    <ChoiceButton
                      key={route.id}
                      label={route.name}
                      detail={route.id === 'E' ? 'RapidRide' : 'Local service'}
                      selected={routeId === route.id}
                      onClick={() => chooseRoute(route.id)}
                      journeyKey={route.id === 'E' ? 'route-e-choice' : 'route-44-choice'}
                    />
                  ))}
                </div>
              </section>
              {routeId ? (
                <section className="border-t border-border p-5 md:p-8" aria-labelledby="direction-heading">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    02 / Direction
                  </p>
                  <h2 id="direction-heading" className="mt-2 text-2xl font-bold tracking-[-0.03em]">
                    Which way are you going?
                  </h2>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {directions.map((direction: DirectionOption, index: number) => (
                      <ChoiceButton
                        key={direction.id}
                        label={direction.label}
                        selected={directionId === direction.id}
                        onClick={() => chooseDirection(direction.id)}
                        journeyKey={index === 0 ? 'direction-choice' : `direction-choice-${direction.id}`}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
              {directionId && routeId ? (
                <section className="border-t border-border p-5 md:p-8" aria-labelledby="stop-heading">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">03 / Stop</p>
                  <h2 id="stop-heading" className="mt-2 text-2xl font-bold tracking-[-0.03em]">
                    Where will you board?
                  </h2>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {stops.map((stop: StopOption, index: number) => (
                      <ChoiceButton
                        key={stop.id}
                        label={stop.name}
                        selected={stopId === stop.id}
                        onClick={() => chooseStop(stop.id)}
                        journeyKey={index === 0 ? 'stop-choice' : `stop-choice-${stop.id}`}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
              {stopId ? (
                <SchedulePane
                  departures={departures}
                  checkedAt={checkedAt}
                  available={available}
                  onView={viewSchedule}
                  onRefresh={refreshSchedule}
                  canView={snapshot.can({ type: 'bus-times.view-schedule' })}
                  hasViewed={hasViewed}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
